import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar } from "../components/Avatar";
import { ChoreGrid } from "../components/ChoreGrid";
import { ChoreRow, type Instance } from "../components/ChoreRow";
import { Jar, jarHue, type JarKind } from "../components/Jar";
import { JarDetail } from "../components/JarDetail";
import { AskForCash, LogSpending } from "../components/KidActions";
import { PaydayCard, PayingOut } from "../components/Payday";
import { ThresholdBar } from "../components/ThresholdBar";
import {
  api,
  ApiError,
  type Bucket,
  type Chart,
  type Child,
  type Collectable,
  type Goal,
  type HistoryRow,
  type Me,
} from "../lib/api";
import { dayComplete, playJingle, unlockJingle } from "../lib/jingle";
import { pourAt, pourComplete, pourDuration, prefersReducedMotion } from "../lib/pour";
import { amountReading, formatCents } from "../lib/money";
import { useLiveData } from "../lib/useLiveData";

/* The child's home screen.
 *
 * One screen answers the three questions a five-year-old actually has: what do
 * I have, what do I have to do today, and how do those connect. Everything
 * else lives a tap away.
 */
export function KidHome({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const childId = me.childId!;
  const [child, setChild] = useState<Child | null>(null);
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [chart, setChart] = useState<Chart | null>(null);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [panel, setPanel] = useState<"cash" | "spend" | null>(null);
  const [panelBucket, setPanelBucket] = useState<string | undefined>();
  const [openJar, setOpenJar] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [cheer, setCheer] = useState(false);
  /* Payday, mid-pour: which week is being handed over and how much of it has
     landed in each jar so far. Null the rest of the time, which is nearly
     always -- this runs for about two seconds a week. */
  const [pour, setPour] = useState<{
    periodId: string;
    poured: Record<string, number>;
    activeBucketId: string | null;
  } | null>(null);
  const frame = useRef<number | null>(null);

  const load = useCallback(async (): Promise<Chart | null> => {
    let nextChart: Chart | null = null;
    try {
      const [c, b, g] = await Promise.all([
        api.get<Child>(`/children/${childId}`),
        api.get<Bucket[]>(`/children/${childId}/buckets`),
        api.get<{ goals: Goal[] }>(`/children/${childId}/goals`),
      ]);
      setChild(c);
      setBuckets(b);
      setGoal(g.goals.find((x) => x.status === "active") ?? g.goals[0] ?? null);
      // Gated server-side too; an empty list is the correct answer for a
      // child too young to see history, not an error.
      try {
        setHistory((await api.get<{ rows: HistoryRow[] }>(
          `/children/${childId}/history`)).rows);
      } catch {
        setHistory([]);
      }
      // A missing schedule is normal before a parent sets one up, so a 404
      // here leaves the jars visible rather than blanking the screen.
      try {
        nextChart = await api.get<Chart>(`/children/${childId}/chart`);
      } catch {
        nextChart = null;
      }
      setChart(nextChart);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load your money.");
    }
    return nextChart;
  }, [childId]);

  useLiveData(load);

  /* Ticking the last job off is worth a noise. Everything below exists to
     make sure it is the RIGHT moment: the child's own tap, the day actually
     complete, and once. */

  /* Once a day, per child. Without this the jingle is a toy: untick the last
     job, tick it again, fanfare, repeat -- which is funny for a minute and
     then is the reason a parent turns the tablet's sound off. It also keeps a
     reload, or a second device, from replaying a morning already celebrated.
     Reduced to a no-op wherever localStorage throws (private windows), which
     costs a repeat and breaks nothing. */
  function claimCheer(localDate: string): boolean {
    const key = `allowance:cheered:${childId}:${localDate}`;
    try {
      if (localStorage.getItem(key)) return false;
      localStorage.setItem(key, "1");
    } catch {
      /* no storage; let it sound */
    }
    return true;
  }

  async function toggle(instance: Instance) {
    setBusyId(instance.id);
    setError(null);
    // Which way this tap goes is the server's answer, not a guess from the
    // status. A self-certifying job sits at "approved" the moment it is
    // tapped, and reading the direction off the status alone would try to
    // claim it a second time and be told it was already done.
    const claiming = instance.canClaim;
    // Opened here, inside the tap itself: a browser will not start audio
    // except from a gesture, and by the time we know the day is finished the
    // gesture is two round trips old.
    if (claiming && child?.playJingle !== false) unlockJingle();
    try {
      const action = claiming ? "claim" : "unclaim";
      await api.post(`/chore-instances/${instance.id}/${action}`);
      const fresh = await load();
      if (claiming && fresh && dayComplete(fresh) && claimCheer(fresh.todayLocalDate)) {
        // The banner still appears with the sound switched off: what is
        // being turned off is the noise, not the congratulations.
        if (child?.playJingle !== false) playJingle();
        setCheer(true);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That didn't work.");
    } finally {
      setBusyId(null);
    }
  }

  /* ── payday ────────────────────────────────────────────────────────── */

  const toCollect = chart?.toCollect ?? [];
  const collecting = toCollect[0] ?? null;

  /* What the jars must NOT show yet.
   *
   * The money is already in them -- it posted at payday and the ledger has
   * carried it since. Holding the figure back is a statement about this
   * screen, not about the money: it is the difference between an allowance
   * that turned up while nobody was looking and one that was handed over.
   * Every uncollected week counts, not just the one on the card, or a child
   * with two weeks waiting would watch the second one pour into a jar that
   * had already quietly counted it. */
  const heldBack: Record<string, number> = {};
  for (const week of toCollect) {
    for (const part of week.parts) {
      heldBack[part.bucketId] = (heldBack[part.bucketId] ?? 0) + part.amountCents;
    }
  }

  function jarCents(bucketId: string, balanceCents: number): number {
    return balanceCents - (heldBack[bucketId] ?? 0) + (pour?.poured[bucketId] ?? 0);
  }

  // A pour left running while the screen goes away would keep calling
  // setState into nothing.
  useEffect(() => () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
  }, []);

  if (error && !child) {
    return <Shell className="kid"><p role="alert">{error}</p></Shell>;
  }
  if (!child) return <Shell className="kid"><p>Loading&hellip;</p></Shell>;

  const showCash = child.showCashPictures;
  const seesTotalOnly = child.capabilities.sees_total_only;
  const jarColumns = buckets.length > 3 ? 2 : Math.max(1, buckets.length);
  /* One board per row. A second row of jars standing on nothing is a row of
     jars floating, which is the thing the shelf exists to stop. */
  const jarRows: Bucket[][] = [];
  for (let at = 0; at < buckets.length; at += jarColumns) {
    jarRows.push(buckets.slice(at, at + jarColumns));
  }

  /* ── payday, continued ─────────────────────────────────────────────── */

  async function collect(week: Collectable) {
    // Opened inside the tap: a browser will not start audio except from a
    // gesture, and the pour is a round trip away by the time we could ask.
    if (child?.playJingle !== false) unlockJingle();
    setError(null);
    setFlash(null);
    setBusyId(week.periodId);
    try {
      await api.post(`/periods/${week.periodId}/collect`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not fetch your money.");
      setBusyId(null);
      return;
    }
    setBusyId(null);
    if (child?.playJingle !== false) playJingle();

    // Straight to the answer for anyone who has asked not to be moved about.
    // They still get the card, the figures and the jars going up -- what they
    // do not get is two seconds of it sliding.
    if (prefersReducedMotion()) {
      setPour({ periodId: week.periodId, ...pourComplete(week.parts) });
      await load();
      setPour(null);
      return;
    }

    const started = performance.now();
    const total = pourDuration(week.parts);
    await new Promise<void>((resolve) => {
      const step = () => {
        const at = pourAt(week.parts, performance.now() - started);
        setPour({ periodId: week.periodId, poured: at.poured,
                  activeBucketId: at.activeBucketId });
        if (at.done) { resolve(); return; }
        frame.current = requestAnimationFrame(step);
      };
      frame.current = requestAnimationFrame(step);
      // A tab put to sleep mid-pour never gets another frame. The jars would
      // sit half full until something else reloaded them, so end it anyway.
      setTimeout(resolve, total + 1500);
    });

    // Reload BEFORE clearing the pour: the fresh chart drops this week from
    // toCollect, so heldBack loses exactly what `poured` was adding. Clearing
    // first would flash the pre-payday figure for a frame.
    setPour({ periodId: week.periodId, ...pourComplete(week.parts) });
    await load();
    setPour(null);
  }

  const openJarBucket = buckets.find((b) => b.id === openJar) ?? null;

  return (
    <Shell className="kid">
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 22,
        }}
      >
        <Avatar name={child.avatar} size={62} ring={child.color} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 26 }}>Hi {child.displayName}!</h1>
          <p style={{ margin: 0, color: "var(--ink-2)", fontSize: 14 }}>
            {new Date().toLocaleDateString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
        <button
          onClick={onSignOut}
          style={{ minHeight: 44, padding: "0 14px", fontSize: 14 }}
        >
          Done
        </button>
      </header>

      {/* THE JARS */}
      <section className="card" style={{ marginBottom: 16 }}>
        {seesTotalOnly && (
          <div style={{ display: "grid", justifyItems: "center", gap: 6,
                        marginBottom: 12 }}>
            <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>
              You have
            </span>
            <span className="money" style={{ fontSize: 44, fontWeight: 700 }}>
              {formatCents(child.balances.bankCents)}
            </span>
          </div>
        )}

        {/* The jars sit on a shelf rather than floating in the plain card --
            a jar you could pick up needs something to be sitting on. Four
            jars wrap to two rows on a phone rather than shrinking to four
            thin slivers: a jar you cannot see the coins in has stopped doing
            its job.

            The amount is printed on the shelf EDGE rather than under the jar,
            because the board's front lip is drawn OVER the jars standing on it
            -- that occlusion is what makes it a shelf rather than a plank --
            and it would cut a figure sitting there in half. The jar carries
            its own name on the tape stuck to its glass. */}
        {jarRows.map((row, at) => (
          <div className="shelf-unit" key={row[0]?.id ?? at}>
            <div className="shelf">
              <div
                className="shelf-jars"
                style={{ gridTemplateColumns: `repeat(${jarColumns}, 1fr)` }}
              >
                {row.map((b) => (
                  <Jar
                    key={b.id}
                    kind={b.kind as JarKind}
                    name={b.name}
                    cents={jarCents(b.id, b.balanceCents)}
                    showCash={showCash}
                    open={openJar === b.id}
                    receiving={pour?.activeBucketId === b.id}
                    onClick={() => {
                      setFlash(null);
                      setOpenJar(openJar === b.id ? null : b.id);
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="shelf-lip" aria-hidden="true" />
            <div
              className="shelf-reads"
              style={{ gridTemplateColumns: `repeat(${jarColumns}, 1fr)` }}
            >
              {row.map((b) => (
                /* Aria-hidden: the jar's own label already reads "Save,
                   $18.75", and a screen reader meeting the figure twice would
                   be hearing the shelf, not the money. */
                <span
                  key={b.id}
                  aria-hidden="true"
                  className="money shelf-read"
                  style={{ color: jarHue(b.kind).deep }}
                >
                  {formatCents(jarCents(b.id, b.balanceCents))}
                </span>
              ))}
            </div>
          </div>
        ))}

        {openJarBucket && (
          <JarDetail
            child={child}
            bucket={openJarBucket}
            buckets={buckets}
            goal={goal}
            onClose={() => setOpenJar(null)}
            onMoved={(message) => {
              setFlash(message);
              void load();
            }}
            onAskCash={(bucketId) => {
              setPanelBucket(bucketId);
              setPanel("cash");
            }}
            onLogSpend={(bucketId) => {
              setPanelBucket(bucketId);
              setPanel("spend");
            }}
          />
        )}

        {flash && (
          <p
            role="status"
            style={{
              margin: "12px 0 0", padding: "10px 14px",
              background: "var(--save-soft)", color: "var(--save-deep)",
              borderRadius: "var(--r-md)", fontWeight: 600, textAlign: "center",
            }}
          >
            {flash}
          </p>
        )}

        {child.balances.walletCents > 0 && (
          <p
            style={{
              margin: "16px 0 0",
              padding: "10px 14px",
              background: "var(--wallet-soft)",
              borderRadius: "var(--r-md)",
              fontSize: 15,
              fontWeight: 600,
              color: "var(--ink-2)",
              textAlign: "center",
            }}
          >
            👛 In your pocket:{" "}
            <span className="money">{formatCents(child.balances.walletCents)}</span>
          </p>
        )}
        {(child.capabilities.can_request_cashout ||
          child.capabilities.can_log_spending) && (
          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
            {child.capabilities.can_request_cashout && (
              <button
                onClick={() => {
                  setPanelBucket(undefined);
                  setPanel(panel === "cash" ? null : "cash");
                }}
                style={{ flex: "1 1 140px", minHeight: 56 }}
              >
                💵 Ask for cash
              </button>
            )}
            {child.capabilities.can_log_spending && (
              <button
                onClick={() => {
                  setPanelBucket(undefined);
                  setPanel(panel === "spend" ? null : "spend");
                }}
                style={{ flex: "1 1 140px", minHeight: 56 }}
              >
                🧾 I spent something
              </button>
            )}
          </div>
        )}

        {child.balances.holdCents > 0 && (
          <p
            style={{
              margin: "10px 0 0",
              fontSize: 14,
              color: "var(--ink-3)",
              textAlign: "center",
            }}
          >
            <span className="money">{formatCents(child.balances.holdCents)}</span>{" "}
            waiting for a grown-up to hand over
          </p>
        )}
      </section>

      {/* TODAY'S JOBS */}
      {chart && (
        <section className="card" style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <h2 style={{ fontSize: 20 }}>Today&rsquo;s jobs</h2>
            {/* A done-of-total tally needs something to total. "0/0" beside an
                empty list reads as a broken counter, not as an empty day --
                and on a child with no chores set up at all it is the only
                thing on the card that looks like a number gone wrong. */}
            {chart.todayTotal > 0 && (
              <span className="money" style={{ fontWeight: 700, color: "var(--ink-2)" }}>
                {chart.todayDone}/{chart.todayTotal}
              </span>
            )}
          </div>

          {chart.today.length === 0 ? (
            /* Two different empty days. A child with a week of chores and none
               due today has earned "enjoy it"; a child nobody has given a job
               to has not, and telling them to enjoy a day off they never had
               hides the fact that a grown-up still has setting-up to do. */
            <p style={{ color: "var(--ink-2)", margin: 0 }}>
              {chart.instances.length === 0
                ? "No jobs yet — a grown-up will add some."
                : "Nothing to do today. Enjoy it!"}
            </p>
          ) : (
            <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
              {chart.today.map((i) => (
                <ChoreRow
                  key={i.id}
                  instance={i}
                  onToggle={toggle}
                  busy={busyId === i.id}
                />
              ))}
            </div>
          )}

          {/* The jingle in words, for a tablet on silent, a child who does not
              hear it, and the second or two afterwards when they want to know
              that the noise meant something good. Tied to the chart rather
              than to a timer, so unticking a job takes it back down. */}
          {cheer && dayComplete(chart) && (
            <p
              role="status"
              style={{
                margin: "0 0 16px", padding: "10px 14px",
                background: "var(--save-soft)", color: "var(--save-deep)",
                borderRadius: "var(--r-md)", fontWeight: 700,
                textAlign: "center", fontSize: 17,
              }}
            >
              All done for today! 🎉
            </p>
          )}

          {/* Payday. Above the "waiting on a grown-up" card on purpose: money
              that is here and yours is a better first thing to read than money
              that is still being decided, and a child with both wants the
              happy one first. */}
          {pour !== null && collecting?.periodId === pour.periodId ? (
            <PayingOut
              collectable={collecting}
              pouredTotal={Object.values(pour.poured).reduce((a, b) => a + b, 0)}
              activeName={
                buckets.find((b) => b.id === pour.activeBucketId)?.name ?? null
              }
            />
          ) : (
            collecting && (
              <PaydayCard
                collectable={collecting}
                onCollect={() => void collect(collecting)}
                busy={busyId === collecting.periodId}
              />
            )
          )}

          {/* Last week, still with a grown-up.
             *
             * It sits above the bar on purpose. The bar is about the week
             * being lived; this is about money that was supposed to arrive
             * and did not, which is the more urgent question by far and the
             * one a child will otherwise ask out loud. Saying a PERSON is
             * deciding -- not the app, not a rule -- is the whole point:
             * there is somebody to go and talk to. */}
          {chart.pendingReview.map((p) => (
            <div
              key={p.periodKey}
              style={{
                margin: "0 0 16px",
                padding: "12px 14px",
                background: "var(--spend-soft)",
                border: "2px solid var(--spend)",
                borderRadius: "var(--r-md)",
              }}
            >
              <p style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 16,
                          fontWeight: 600, color: "var(--spend-deep)" }}>
                Your <span className="money">{formatCents(p.amountCents)}</span> for{" "}
                {p.periodLabel} is waiting
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--ink-2)" }}>
                Not all the jobs were ticked off, so a grown-up is having a look.
                Ask them about it!
              </p>
            </div>
          ))}

          <ThresholdBar
            earnedBp={chart.earnedBp}
            thresholdBp={chart.thresholdBp}
            amountCents={chart.amountAtStakeCents}
            jobsStillNeeded={chart.jobsStillNeeded}
            met={chart.meetsThreshold}
            payoutAt={chart.payoutAt}
          />

          {/* The bar, opened out. The bar says whether the week is going to
              pay; this says which days it was actually earned on, which is
              the question that comes next -- and it is the same drawing a
              grown-up corrects on the other side of the app. */}
          {chart.instances.length > 0 && (
            <div style={{ marginTop: 18, paddingTop: 16,
                          borderTop: "1px solid var(--paper-edge)" }}>
              <h3 style={{ fontSize: 15, marginBottom: 10 }}>Your week</h3>
              <ChoreGrid
                instances={chart.instances}
                days={chart.days}
                todayLocalDate={chart.todayLocalDate}
              />
            </div>
          )}
        </section>
      )}

      {/* GOAL */}
      {goal && (
        <section className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, marginBottom: 10 }}>
            Saving for {goal.name}
          </h2>
          <div
            style={{
              height: 20,
              background: "var(--paper-edge)",
              border: "2px solid var(--ink)",
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${goal.progressBp / 100}%`,
                background: "var(--save)",
                transition: "width 320ms cubic-bezier(.2,.9,.3,1)",
              }}
            />
          </div>
          <p style={{ margin: "10px 0 0", fontSize: 15, color: "var(--ink-2)" }}>
            {goal.status === "achieved" ? (
              <strong style={{ color: "var(--save-deep)" }}>
                You did it! You saved enough.
              </strong>
            ) : (
              <>
                <span className="money">{formatCents(goal.savedCents)}</span> of{" "}
                <span className="money">{formatCents(goal.targetCents)}</span> &mdash;{" "}
                <span className="money">{formatCents(goal.remainingCents)}</span> to go
              </>
            )}
          </p>
        </section>
      )}

      {panel === "cash" && (
        <AskForCash
          childId={childId}
          buckets={buckets}
          initialBucketId={panelBucket}
          onCancel={() => setPanel(null)}
          onDone={() => {
            setPanel(null);
            void load();
          }}
        />
      )}
      {panel === "spend" && (
        <LogSpending
          childId={childId}
          buckets={buckets}
          initialBucketId={panelBucket}
          onCancel={() => setPanel(null)}
          onDone={() => {
            setPanel(null);
            void load();
          }}
        />
      )}

      {history.length > 0 && (
        <section className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, marginBottom: 10 }}>What happened</h2>
          <div style={{ display: "grid", gap: 2 }}>
            {history.map((row) => (
              <div
                key={row.id}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "9px 2px",
                  borderBottom: "1px solid var(--paper-edge)",
                  opacity: row.isReversed ? 0.5 : 1,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{
                    display: "block", fontSize: 15,
                    textDecoration: row.isReversed ? "line-through" : "none" }}>
                    {row.label}
                    {row.memo && (
                      <span style={{ color: "var(--ink-3)" }}>, {row.memo}</span>
                    )}
                  </span>
                  {row.localDate && (
                    <span className="money" style={{ fontSize: 12, color: "var(--ink-3)" }}>
                      {row.localDate}
                    </span>
                  )}
                </span>
                <span className="money" style={{
                  fontWeight: 700, color: amountReading(row).tone }}>
                  {amountReading(row).text}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600 }}>
          {error}
        </p>
      )}
    </Shell>
  );
}

export function Shell({
  children,
  className,
}: {
  children: React.ReactNode;
  /** "kid" scopes the child's two-face type to this screen. The parent's
   *  passbook and the signed-out states share this shell and must not get it. */
  className?: string;
}) {
  return (
    <main
      className={className}
      style={{ maxWidth: 560, margin: "0 auto", padding: "20px 16px 48px" }}
    >
      {children}
    </main>
  );
}
